import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { CommonChartConfig } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

export namespace Crypto {
  export namespace Quotes {
    const CONTEXT_ID = 'equity:crypto:quotes'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      ticker: string
      title?: string
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        name: string
        series_id: string
        data: {
          date: string
          value: number
        }[]
      }[]
      title: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: '/api/charts/crypto/quotes',
          method: 'POST',
          data: { ticker: config.ticker },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: { valueDecimals: 2 },
        showInLegend: false,
        yAxis: 'default',
      }))

      const yAxis = { id: 'default', opposite: false, title: { text: '' } }

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            chart: { zoomType: 'x' },
            series,
            yAxis,
            tooltip: { shared: false, split: true },
          },
        ]),
        stockTools: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace BuyBitcoinWorldWide {
    const CONTEXT_ID = 'equity:crypto:buy_bitcoin_worldwide'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'stock_to_flow' | 'segwit'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.chart === 'segwit'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          ...(config.chart === 'stock_to_flow'
            ? { yAxis: [{ type: 'logarithmic' }] }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart === 'segwit' ? '%' : '',
            }),
          },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)
  }

  export namespace TheBlockCrypto {
    const CONTEXT_ID = 'equity:crypto:the_block_crypto'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'total_stablecoin_supply'
        | 'share_of_total_stablecoin_supply'
        | 'tether_supply_by_blockchain'
        | 'usdc_supply_by_blockchain'
        | 'average_transaction_fee_comparison'
        | 'percentage_of_ethereum_supply_active'
        | 'percentage_of_bitcoin_supply_active'
        | 'lightning_network_capacity'
        | 'top_20_gas_using_smart_contracts'
        | 'share_of_transaction_fee_from_total_revenue_ethereum'
        | 'share_of_transaction_fee_from_total_revenue_bitcoin'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'total_stablecoin_supply':
            return getTotalStablecoinSupplyChartProps(config, data)
          case 'share_of_total_stablecoin_supply':
            return getShareOfTotalStablecoinSupplyChartProps(config, data)
          case 'lightning_network_capacity':
            return getLightningNetworkCapacity(config, data)
          case 'top_20_gas_using_smart_contracts':
            return getTop20GasUsingSmartContracts(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getTotalStablecoinSupplyChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          plotOptions: { area: { stacking: 'normal' } },
          tooltip: { pointFormatter: magnitudePointFormatter() },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    const getShareOfTotalStablecoinSupplyChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'normal' } },
          tooltip: { xDateFormat: '%A, %b %e, %Y' },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    const getLightningNetworkCapacity = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: [
            { id: 'primary', opposite: false, title: { text: '' } },
            { id: 'secondary', opposite: true, title: { text: '' } },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
          yAxis: ['primary', 'secondary'],
        }
      )(config, data)

    const getTop20GasUsingSmartContracts = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        data: s.data.map(({ value }) => [s.name, value]),
        id: `series-${idx}`,
        lineWidth: 2,
        marker: { enabled: false },
        name: s.name,
        type: 'column',
        yAxis: 'default',
      }))

      const yAxis = [{ id: 'default', opposite: false, title: { text: ' ' } }]

      const xAxis = [
        { type: 'category', categories: series.map((s) => s.name) },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          commonChartOptions,
          getDataSources(data.data_sources),
          {
            legend: { maxHeight: 80 },
            plotOptions: { column: { pointWidth: 15 } },
            series,
            tooltip: {
              shared: true,
              split: false,
              pointFormatter: magnitudePointFormatter(),
              xDateFormat: '%B %Y',
            },
            xAxis,
            yAxis,
          },
        ]),
        useRegularChart: true,
        stockTools: true,
      }
    }

    const isPercentChart = (chart: string) =>
      chart === 'share_of_total_stablecoin_supply' ||
      chart === 'percentage_of_ethereum_supply_active' ||
      chart === 'percentage_of_bitcoin_supply_active' ||
      chart === 'share_of_transaction_fee_from_total_revenue_ethereum' ||
      chart === 'share_of_transaction_fee_from_total_revenue_bitcoin'

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          ...(config.chart === 'average_transaction_fee_comparison'
            ? {
                yAxis: [{ id: 'default', title: { text: 'USD' } }],
              }
            : {}),
          ...(isPercentChart(config.chart)
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: isPercentChart(config.chart) ? '%' : '',
            }),
          },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)
  }

  export namespace BlockchainCom {
    const CONTEXT_ID = 'equity:crypto:blockchain_com'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'avg_confirmation_time'
        | 'blocks_size'
        | 'difficulty'
        | 'hash_rate'
        | 'market_cap'
        | 'market_price'
        | 'median_confirmation_time'
        | 'n_transactions_total'
        | 'nvt'
        | 'total_bitcoins'
        | 'trade_volume'
        | 'transaction_fees'
        | 'transactions_per_second'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: [
            {
              id: 'default',
              title: {
                text:
                  config.chart === 'blocks_size'
                    ? 'MB'
                    : config.chart === 'transactions_per_second'
                    ? 'seconds'
                    : config.chart === 'avg_confirmation_time' ||
                      config.chart === 'median_confirmation_time'
                    ? 'minutes'
                    : config.chart === 'hash_rate'
                    ? 'TH/s'
                    : config.chart === 'transaction_fees'
                    ? 'BTC'
                    : '',
              },
            },
          ],
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)
  }

  export namespace DefiLlama {
    const CONTEXT_ID = 'equity:crypto:defi_llama'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'all_protocols'
        | 'aave'
        | 'uniswap'
        | 'curve'
        | 'compound'
        | 'tvl_chains'
        | 'rankings_lending'
        | 'rankings_dexes'
        | 'rankings_index'
        | 'rankings_chains'
        | 'tvl_categories'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'tvl_chains':
            return getTvlChains(config, data)
          case 'tvl_categories':
            return getTvlCategories(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getTvlChains = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'normal' } },
          xAxis: { labels: { format: '{value:%m/%y}' } },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, data)

    const getTvlCategories = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        data: s.data.map(({ value }) => [capitalizeString(s.name), value]),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: capitalizeString(s.name),
        type: 'column',
        yAxis: 'default',
      }))

      const yAxis = [{ id: 'default', opposite: false, title: { text: ' ' } }]

      const xAxis = [
        {
          type: 'category',
          categories: series.map((s) => capitalizeString(s.name)),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          commonChartOptions,
          getDataSources(data.data_sources),
          {
            legend: { maxHeight: 80 },
            plotOptions: { column: { pointWidth: 25 } },
            series,
            tooltip: {
              shared: true,
              split: false,
              pointFormatter: magnitudePointFormatter(),
              xDateFormat: '%B %Y',
            },
            xAxis,
            yAxis,
          },
        ]),
        useRegularChart: true,
        stockTools: true,
      }
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%A, %b %e, %Y',
          },
          xAxis: { labels: { format: '{value:%m/%y}' } },
        },
        {
          name: Array(data.data.length).fill(''),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }

  export namespace CoinMarketCap {
    const CONTEXT_ID = 'equity:crypto:coin_market_cap'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'price_usd_ethereum'
        | 'market_cap_usd_ethereum'
        | 'price_usd_bitcoin'
        | 'market_cap_usd_bitcoin'
        | 'total_cryptocurrency_market_cap'
        | 'major_crypto_assets_by_percentage_of_total_market_capitalization'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'total_cryptocurrency_market_cap':
            return getTotalCryptocurrencyMarketCapChartProps(config, data)
          case 'major_crypto_assets_by_percentage_of_total_market_capitalization':
            return getStackedPercentageChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const formatSeriesNames = (response: ChartData) => ({
      ...response,
      data: response.data.map((s) => ({
        ...s,
        name: capitalizeString(s.name.replaceAll('_', ' ')),
      })),
    })

    const getTotalCryptocurrencyMarketCapChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: { pointFormatter: magnitudePointFormatter() },
          yAxis: [
            { id: 'primary', opposite: false, title: { text: '' } },
            { id: 'secondary', opposite: true, title: { text: '' } },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
          yAxis: ['primary', 'secondary'],
        }
      )(config, formatSeriesNames(data))

    const getStackedPercentageChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'normal' } },
          tooltip: {
            shared: true,
            split: false,
            xDateFormat: '%A, %b %e, %Y',
          },
        },
        { type: Array(data.data.length).fill('area') }
      )(config, formatSeriesNames(data))

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%a, %b %e, %Y',
          },
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, formatSeriesNames(data))
  }
}
